import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import RequireAuth from "../auth/RequireAuth";
import { useAuth } from "../auth/AuthProvider";
import Login from "../pages/Login";
import AdminLayout from "../layouts/AdminLayout";
import UserLayout from "../layouts/UserLayout";
import PublicLayout from "../layouts/PublicLayout";
import DevGate from "../components/DevGate";
import AdminDashboard from "../pages/admin/Dashboard";
import UserDashboard from "../pages/user/Dashboard";
import ClientsAll from "../pages/admin/ClientsAll";
import UsersAll from "../pages/admin/UsersAll";
import Model from "../pages/admin/Model";
import Settings from "../pages/admin/Settings";
import SupportDocs from "../pages/admin/SupportDocs";
import ClientDetail from "../pages/admin/ClientDetail";
import AdminProjectDetail from "../pages/admin/ProjectDetail";

// User-specific pages
import ApiKeys from "../pages/user/ApiKeys";
import ProjectsAll from "../pages/user/ProjectsAll";
import UserProjectDetail from "../pages/user/ProjectDetail";
import CompareProjects from "../pages/user/CompareProjects";
import LoadPortfolios from "../pages/user/LoadPortfolios";
import LoadPortfolioDetail from "../pages/user/LoadPortfolioDetail";
import Loads from "../pages/user/Loads";
import LoadDetail from "../pages/user/LoadDetail";
import PartnerClientsAll from "../pages/user/PartnerClientsAll";
import PartnerClientDetail from "../pages/user/PartnerClientDetail";

// Public pages
import GridMapDashboard from "../pages/public/GridMapDashboard";
import GridRegionDetail from "../pages/public/GridRegionDetail";
import GeneratorDetail from "../pages/public/GeneratorDetail";
import PipelineImpactPage from "../pages/public/PipelineImpactPage";
import Register from "../pages/public/Register";

// Auth pages (public, outside RequireAuth)
import SetPassword from "../pages/auth/SetPassword";
import ResetPassword from "../pages/auth/ResetPassword";
import ForgotPassword from "../pages/auth/ForgotPassword";
import AmazonCallback from "../pages/AmazonCallback";

// Private route wrapper component
const Private: React.FC<{ children: JSX.Element }> = ({ children }) => {
    const { token } = useAuth();
    return token ? children : <Navigate to="/login" replace />;
};

// Partners go to clients, regular users go to dashboard (portfolio view)
const AppIndex: React.FC = () => {
    const { isPartner } = useAuth();
    return <Navigate to={isPartner ? "clients" : "dashboard"} replace />;
};

const enablePublicPages = process.env.REACT_APP_ENABLE_PUBLIC_PAGES === 'true';

const AppRoutes: React.FC = () => {
    return (
        <Routes>
            {/* Public grid / generator pages (only when enabled, gated in dev) */}
            {enablePublicPages && (
                <Route element={<DevGate><PublicLayout /></DevGate>}>
                    <Route path="/grid" element={<GridMapDashboard />} />
                    <Route path="/grid/rankings" element={<Navigate to="/grid?view=rankings" replace />} />
                    <Route path="/grid/states" element={<Navigate to="/grid?view=baseline" replace />} />
                    <Route path="/grid/:baCode" element={<GridRegionDetail />} />
                    <Route path="/generators" element={<Navigate to="/grid?view=generators" replace />} />
                    <Route path="/generators/:plantId/:generatorId" element={<GeneratorDetail />} />
                    <Route path="/pipeline-impacts" element={<PipelineImpactPage />} />
                    {/* Registration disabled — invite-only */}
                </Route>
            )}

            {/* Root redirects to grid map (public) or login (authenticated only) */}
            <Route path="/" element={<Navigate to={enablePublicPages ? "/grid" : "/login"} replace />} />
            <Route path="/login" element={enablePublicPages ? <DevGate><Login /></DevGate> : <Login />} />

            {/* Public auth pages (invite, password reset) */}
            <Route path="/auth/amazon/callback" element={<AmazonCallback />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />

            {/* Admin protected area */}
            <Route element={<RequireAuth adminOnly />}>
                <Route path="/admin" element={<AdminLayout />}>
                    <Route path="dashboard" element={<AdminDashboard />} />
                    {/* Clients */}
                    <Route path="clients" element={<ClientsAll />} />
                    <Route path="clients/:clientId" element={<ClientDetail />} />
                    {/* Users */}
                    <Route path="users" element={<UsersAll />} />
                    {/* Model Runs */}
                    <Route path="model" element={<Model />} />
                    {/* API Keys */}
                    <Route path="api-keys" element={<ApiKeys />} />
                    {/* Settings */}
                    <Route path="settings" element={<Settings />} />
                    {/* Support */}
                    <Route path="support" element={<SupportDocs />} />
                    {/* Loads (the RE-Project dual) */}
                    <Route path="loads" element={<Loads />} />
                    <Route path="loads/:loadId" element={<LoadDetail />} />
                    {/* Load portfolios (deprecated — superseded by Loads) */}
                    <Route path="load-portfolios" element={<LoadPortfolios />} />
                    <Route path="load-portfolios/:portfolioId" element={<LoadPortfolioDetail />} />
                    {/* Default → dashboard */}
                    <Route index element={<Navigate to="dashboard" replace />} />
                    <Route path="projects/:projectId" element={<Private><AdminProjectDetail /></Private>} />
                    {/* <Route path="projects/:projectId/results" element={<ProjectDetailResults />} /> */}
                </Route>
            </Route>

            {/* User protected area */}
            <Route element={<RequireAuth />}>
                <Route path="/app" element={<UserLayout />}>
                    <Route path="dashboard" element={<UserDashboard />} />
                    {/* Portfolios — redirect to dashboard (tags are the new portfolios) */}
                    <Route path="portfolios" element={<Navigate to="/app/dashboard" replace />} />
                    {/* Partner client management */}
                    <Route path="clients" element={<PartnerClientsAll />} />
                    <Route path="clients/:clientId" element={<PartnerClientDetail />} />
                    {/* Projects */}
                    <Route path="projects" element={<ProjectsAll />} />
                    <Route path="projects/:projectId" element={<UserProjectDetail />} />
                    <Route path="compare" element={<CompareProjects />} />
                    {/* API Keys */}
                    <Route path="api-keys" element={<ApiKeys />} />
                    {/* Loads (the RE-Project dual) */}
                    <Route path="loads" element={<Loads />} />
                    <Route path="loads/:loadId" element={<LoadDetail />} />
                    {/* Load portfolios (deprecated — superseded by Loads) */}
                    <Route path="load-portfolios" element={<LoadPortfolios />} />
                    <Route path="load-portfolios/:portfolioId" element={<LoadPortfolioDetail />} />
                    {/* Model Runs */}
                    <Route path="model" element={<Model />} />
                    {/* Settings */}
                    <Route path="settings" element={<Settings />} />
                    {/* Support */}
                    <Route path="support" element={<SupportDocs />} />
                    {/* Default → portfolios for regular users, projects for partners */}
                    <Route index element={<AppIndex />} />
                </Route>
            </Route>
        </Routes>
    );
};

export default AppRoutes;
